<template>
  <v-navigation-drawer
    hide-overlay
    v-model="propModel"
    app
    color="#1e2f4a"
    :clipped="clippedNavbar"
    :permanent="permanentNavbar"
    :temporary="temporaryNavbar"
    :mini-variant="miniVariant"
    :mini-variant-width="miniVariantWidth"
  >
    <ViewAsAdminSelect
      v-if="signedInIsKonekoAdmin"
      compact
      dark
      @change="onViewAsChange"
    />
    <v-list-item class="user_info_drawer">
      <v-list-item-avatar height="70" width="70">
        <v-img :src="getAvatarImage"></v-img>
      </v-list-item-avatar>
      <v-list-item-content>
        <h4 class="mb-0">
          {{ (userProfileData && userProfileData.username) || "…" }}, Lvl:{{}}
        </h4>
      </v-list-item-content>
    </v-list-item>
    <v-list>
      <v-list-item-group color="#1e2f4a">
        <v-list-item
          style="text-decoration: none"
          dark
          v-for="menu in menuListFiltered"
          :key="menu.title"
          :href="menu.external ? menu.href : undefined"
          :to="menu.external ? undefined : menu.link"
          :target="menu.external ? '_blank' : undefined"
          :rel="menu.external ? 'noopener noreferrer' : undefined"
        >
          <v-list-item-action>
            <v-icon>{{ menu.icon }}</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title v-text="menu.title"></v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-item-group>
    </v-list>
    <v-list>
      <v-list-item @click="doLogout" dark>
        <v-list-item-action>
          <v-icon>mdi-logout</v-icon>
        </v-list-item-action>
        <v-list-item-content>Logout</v-list-item-content>
      </v-list-item>
    </v-list>
    <v-list>
      <v-list-item dark>
        <v-list-item-content>
          <v-list-item-title v-text="buildVersion"></v-list-item-title>
          <v-list-item-subtitle v-text="buildNumber"></v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <h5 class="ml-2" style="position: absolute; bottom: 0; color: whitesmoke">
      <strong>©NekoLogic</strong> {{ new Date().getFullYear() }}
    </h5>
  </v-navigation-drawer>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import options from "../../../options";
import {
  canonicalizeKonekoUsername,
  isKonekoAccessControlUser,
  konekoAAdminUsernames,
} from "@/config/konekoAConfig";
import ViewAsAdminSelect from "@/components/Layout/ViewAsAdminSelect.vue";
import {
  clearStoredViewAsUsername,
  dashboardViewAsState,
} from "@/utils/dashboardViewAsSync";
import {getKonekoCapabilities, KONEKO_CAP} from "@/config/konekoVisibility";
import {
  getNewKonekoTimerUrl,
  isLegacyTimerRetired,
} from "@/config/legacyTimerConfig";
import safeAreaInsets from "safe-area-insets";

export default {
  name: "NavDrawer",
  components: {ViewAsAdminSelect},
  props: [
    "sideNav",
    "isLoggedIn",
    "clippedNavbar",
    "permanentNavbar",
    "temporaryNavbar",
    "miniVariant",
    "miniVariantWidth",
  ],
  data() {
    return {
      isEnabled: this.sideNav,
      value: this.isLoggedIn,
      safeAreaOffset: safeAreaInsets.top,
    };
  },
  computed: {
    ...mapGetters(["userProfileData"]),
    signedInIsKonekoAdmin() {
      const u = canonicalizeKonekoUsername(
        this.userProfileData && this.userProfileData.username
      );
      if (!u) return false;
      return konekoAAdminUsernames.some((a) => String(a).toLowerCase() === u);
    },
    /** Liam only, and never while View-as is simulating someone else. */
    showKonekoAccessNav() {
      void dashboardViewAsState.viewingAsPlanningKeyNonSelf;
      if (dashboardViewAsState.viewingAsPlanningKeyNonSelf) return false;
      return isKonekoAccessControlUser(this.userProfileData);
    },
    /** Legacy View Timesheet — Liam only while staff use timer.neko-logic.com. */
    showLegacyTimesheetNav() {
      return isKonekoAccessControlUser(this.userProfileData);
    },
    effectiveNavCapabilities() {
      void dashboardViewAsState.visibilityOverridesRev;
      const preview = dashboardViewAsState.viewingAsPlanningKeyNonSelf;
      const who =
        preview ||
        canonicalizeKonekoUsername(
          this.userProfileData && this.userProfileData.username
        );
      return getKonekoCapabilities(who);
    },
    showKonekoANav() {
      return Boolean(this.effectiveNavCapabilities[KONEKO_CAP.navKonekoAFull]);
    },
    /** Admins keep full Koneko A; others open personal scope only. */
    myBudgetNavLink() {
      if (this.effectiveNavCapabilities[KONEKO_CAP.navKonekoAFull]) {
        return {name: "KonekoA", query: {focus: "my-budget"}};
      }
      return {name: "KonekoA", query: {scope: "self", focus: "my-budget"}};
    },
    menuListFiltered() {
      const caps = this.effectiveNavCapabilities;
      const items = [];
      if (caps[KONEKO_CAP.navKonekoAFull]) {
        items.push({
          icon: "mdi-chart-box",
          title: "Koneko A",
          link: {name: "KonekoA"},
        });
      }
      if (this.showKonekoAccessNav) {
        items.push({
          icon: "mdi-shield-account-outline",
          title: "Koneko access",
          link: {name: "KonekoAAccess"},
        });
      }
      if (caps[KONEKO_CAP.navPayroll]) {
        items.push({
          icon: "mdi-cash-multiple",
          title: "Payroll",
          link: {name: "Payroll"},
        });
      }
      items.push({
        icon: "mdi-view-dashboard",
        title: "Dashboard",
        link: "/dashboard",
      });
      if (caps[KONEKO_CAP.konekoMyBudget]) {
        items.push({
          icon: "mdi-chart-box-outline",
          title: "My budget",
          link: this.myBudgetNavLink,
        });
      }
      if (isLegacyTimerRetired()) {
        items.push({
          icon: "mdi-clock-outline",
          title: "Time & tasks (new)",
          href: getNewKonekoTimerUrl(),
          external: true,
        });
      } else {
        items.push({icon: "mdi-clock", title: "Active Timer", link: "/timer"});
      }
      if (this.showLegacyTimesheetNav) {
        items.push({
          icon: "mdi-calendar-clock",
          title: "Timesheet",
          link: "/timesheet/viewtimesheet",
        });
      }
      items.push(
        {icon: "mdi-book", title: "Projects", link: "/projects"},
        {icon: "mdi-account-multiple", title: "Members", link: "/members"},
        {icon: "mdi-account", title: "Profile", link: "/profile"}
      );
      return items;
    },
    propModel: {
      get() {
        return this.sideNav;
      },
      set(value) {
        this.$emit("update-navigation-prop", value);
      },
    },
    buildVersion() {
      return options.buildType + options.commitHash;
    },
    buildNumber() {
      return options.version || "";
    },
    getAvatarImage() {
      return require("@/assets/image/NekologicSquare.png");
    },
  },
  methods: {
    ...mapActions(["AUTH_LOGOUT_REQUEST"]),
    onViewAsChange() {
      /* sessionStorage + dashboardViewAsState updated in ViewAsAdminSelect */
    },
    doLogout() {
      clearStoredViewAsUsername();
      this.AUTH_LOGOUT_REQUEST();
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.first_app_bar {
  padding: 0px;
  background: linear-gradient(
    0deg,
    rgba(21, 19, 89, 1) 0%,
    rgba(42, 37, 173, 1) 100%
  );
}

.second_app_bar {
  background: linear-gradient(
    0deg,
    rgba(42, 95, 147, 1) 0%,
    rgba(58, 131, 203, 1) 100%
  );
}

.v-item--active {
  color: brown;
  background-color: whitesmoke;
}

.padding-nav-bar {
  padding: 5px;
}

.timer-icon {
  color: black;
}

.center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.timer_button {
  padding: 5px;
}

.progress_linear_indicator {
  border: 5px solid grey;
  border-radius: 20px;
  width: 150px;
}

.space {
  width: 6px;
}

.user_info_drawer {
  display: none;
}

/* Small devices (portrait tablets and large phones, 768px and up) */
@media only screen and (max-width: 1165px) {
  .nav_bar_tittle {
    display: none;
  }

  .progress_linear_indicator {
    width: 125px;
  }
}

/* Small devices (portrait tablets and large phones, 768px and up) */
@media only screen and (max-width: 768px) {
  .user_avatar {
    display: none;
  }

  .user_info {
    display: none;
  }

  .user_info_drawer {
    display: none;
  }
}

@media only screen and (max-width: 640px) {
  .project_timer_avatar {
    display: none;
  }

  .first_app_bar >>> .v-toolbar__content {
    padding: 0px !important;
  }

  .space {
    width: 12px;
  }

  .time_count {
    font-size: 20px;
  }

  .center {
    padding: 5px;
  }
}

/* Extra small devices (phones, 400px and down) */
@media only screen and (max-width: 400px) {
  .user_info_drawer {
    display: none;
  }
}
</style>
